import React, { useState } from "react";
import { Briefcase, Check, Heart, GraduationCap, MapPin, Search, X } from "lucide-react";

export const EMPTY_FILTERS = {
  city: [], professione: [], friendly: [], formed: [], eta: [], lavoro_con: [],
  trauma: "", online: false, agevolate: false, in_studio: false, studio_accessibile: false,
};

export const TRAUMA_OPTIONS = [
  { value: "", label: "Qualsiasi" },
  { value: "più no che sì", label: "Almeno parziale" },
  { value: "più sì che no", label: "Almeno significativa" },
  { value: "sì", label: "Specifica" },
];

const TOGGLES = [
  { key: "in_studio", label: "Riceve in studio" },
  { key: "studio_accessibile", label: "Studio accessibile" },
  { key: "online", label: "Consulenze online" },
  { key: "agevolate", label: "Tariffe agevolate" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const toParams = (q, f) => {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  Object.entries(f).forEach(([k, v]) => {
    if (Array.isArray(v)) v.forEach((x) => params.append(k, x));
    else if (v === true) params.set(k, "true");
    else if (v) params.set(k, v);
  });
  return params;
};

export const fromParams = (sp) => {
  const f = { ...EMPTY_FILTERS };
  Object.keys(EMPTY_FILTERS).forEach((k) => {
    if (Array.isArray(EMPTY_FILTERS[k])) f[k] = sp.getAll(k);
    else if (typeof EMPTY_FILTERS[k] === "boolean") f[k] = sp.get(k) === "true";
    else f[k] = sp.get(k) || "";
  });
  return f;
};

export const countActive = (f) =>
  f.city.length + f.professione.length + f.friendly.length + f.formed.length + f.eta.length + f.lavoro_con.length +
  (f.trauma ? 1 : 0) + TOGGLES.filter((t) => f[t.key]).length;

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm border transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-fucsia ${
        active
          ? "bg-viola text-cream border-viola dark:bg-fucsia dark:border-fucsia"
          : "bg-white/70 border-viola/20 text-viola hover:border-viola/50 dark:bg-viola/40 dark:border-cream/20 dark:text-cream dark:hover:border-cream/50"
      }`}
    >
      {active && <Check size={13} aria-hidden />}
      {children}
    </button>
  );
}

function Section({ title, icon, hint, children, testId }) {
  return (
    <fieldset className="min-w-0" data-testid={testId}>
      <legend className="text-xs font-semibold uppercase tracking-widest text-viola/70 dark:text-cream/70 flex items-center gap-1">
        {icon} {title}
      </legend>
      {hint && <p className="mt-1 text-xs text-viola/60 dark:text-cream/60">{hint}</p>}
      <div className="mt-3">{children}</div>
    </fieldset>
  );
}

function ChipGroup({ options, selected, onToggle, prefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <Chip key={o} active={selected.includes(o)} onClick={() => onToggle(o)} testId={`${prefix}-${slug(o)}`}>
          {o}
        </Chip>
      ))}
    </div>
  );
}

function CityPicker({ cities, selected, onToggle }) {
  const [term, setTerm] = useState("");
  const shown = cities
    .filter((c) => c.toLowerCase().includes(term.toLowerCase()))
    .sort((a, b) => selected.includes(b) - selected.includes(a));
  return (
    <>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-viola/50 dark:text-cream/50" />
        <input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Cerca una città…"
          aria-label="Cerca una città"
          className="w-full rounded-full border border-viola/20 bg-white dark:bg-viola/50 dark:border-cream/20 dark:text-cream pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fucsia"
          data-testid="rete-city-search"
        />
      </div>
      <div className="mt-3 max-h-40 overflow-y-auto pr-1">
        {shown.length ? (
          <ChipGroup options={shown} selected={selected} onToggle={onToggle} prefix="rete-city" />
        ) : (
          <p className="text-sm text-viola/60 dark:text-cream/60">Nessuna città trovata.</p>
        )}
      </div>
    </>
  );
}

export function ReteFiltersPanel({ meta, filters, setFilters, resultCount, onDone }) {
  const toggleIn = (key) => (val) =>
    setFilters((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((v) => v !== val) : [...f[key], val] }));
  return (
    <div className="mt-5 p-5 sm:p-6 rounded-2xl bg-lilla/40 border border-viola/10 dark:bg-viola/30 dark:border-cream/10 grid lg:grid-cols-2 gap-x-10 gap-y-8" data-testid="rete-filters-panel">
      <Section title="Città" icon={<MapPin size={11} />} hint="Puoi sceglierne più di una." testId="rete-filter-city">
        <CityPicker cities={meta.cities} selected={filters.city} onToggle={toggleIn("city")} />
      </Section>
      <Section title="Professione" icon={<Briefcase size={11} />} hint="Almeno una delle professioni scelte." testId="rete-filter-professione">
        <ChipGroup options={meta.professioni || []} selected={filters.professione} onToggle={toggleIn("professione")} prefix="rete-prof" />
      </Section>
      <Section title="Modalità" testId="rete-filter-modalita">
        <div className="flex flex-wrap gap-2">
          {TOGGLES.map((t) => (
            <Chip key={t.key} active={filters[t.key]} onClick={() => setFilters((f) => ({ ...f, [t.key]: !f[t.key] }))} testId={`rete-${t.key}`}>
              {t.label}
            </Chip>
          ))}
        </div>
      </Section>
      <Section title="Friendly con" icon={<Heart size={11} />} hint="Mostra chi è friendly con tutte le aree scelte." testId="rete-filter-friendly">
        <ChipGroup options={meta.groups} selected={filters.friendly} onToggle={toggleIn("friendly")} prefix="rete-friendly" />
      </Section>
      <Section title="Formata su" icon={<GraduationCap size={11} />} hint="Mostra chi ha formazione su tutte le aree scelte." testId="rete-filter-formed">
        <ChipGroup options={meta.groups} selected={filters.formed} onToggle={toggleIn("formed")} prefix="rete-formed" />
      </Section>
      <Section title="Fascia d'età" hint="Almeno una delle fasce scelte." testId="rete-filter-eta">
        <ChipGroup options={meta.eta || []} selected={filters.eta} onToggle={toggleIn("eta")} prefix="rete-eta" />
      </Section>
      <Section title="Lavora con" hint="Almeno una delle configurazioni scelte." testId="rete-filter-lavoro">
        <ChipGroup options={meta.lavoro_con || []} selected={filters.lavoro_con} onToggle={toggleIn("lavoro_con")} prefix="rete-lavoro" />
      </Section>
      <Section title="Esperienza sul trauma" hint="Livello minimo di formazione dichiarato." testId="rete-filter-trauma">
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {TRAUMA_OPTIONS.map((o) => (
            <Chip key={o.label} active={filters.trauma === o.value} onClick={() => setFilters((f) => ({ ...f, trauma: o.value }))} testId={`rete-trauma-${slug(o.label)}`}>
              {o.label}
            </Chip>
          ))}
        </div>
      </Section>
      <div className="lg:col-span-2 flex justify-end">
        <button
          type="button"
          onClick={onDone}
          className="w-full sm:w-auto rounded-full bg-fucsia text-white px-6 py-3 text-sm font-semibold hover:opacity-90 transition-opacity"
          data-testid="rete-show-results"
        >
          Mostra {resultCount} risultati
        </button>
      </div>
    </div>
  );
}

export function ActiveFilters({ filters, setFilters }) {
  const tags = [
    ...filters.city.map((v) => ({ key: "city", v, label: v })),
    ...filters.professione.map((v) => ({ key: "professione", v, label: v })),
    ...filters.friendly.map((v) => ({ key: "friendly", v, label: `Friendly: ${v}` })),
    ...filters.formed.map((v) => ({ key: "formed", v, label: `Formata: ${v}` })),
    ...filters.eta.map((v) => ({ key: "eta", v, label: v })),
    ...filters.lavoro_con.map((v) => ({ key: "lavoro_con", v, label: v })),
    ...(filters.trauma ? [{ key: "trauma", label: `Trauma: ${TRAUMA_OPTIONS.find((o) => o.value === filters.trauma).label}` }] : []),
    ...TOGGLES.filter((t) => filters[t.key]).map((t) => ({ key: t.key, label: t.label })),
  ];
  if (!tags.length) return null;
  const remove = (t) => setFilters((f) => ({
    ...f,
    [t.key]: Array.isArray(f[t.key]) ? f[t.key].filter((x) => x !== t.v) : EMPTY_FILTERS[t.key],
  }));
  return (
    <div className="mt-4 flex flex-wrap gap-2" data-testid="rete-active-filters">
      {tags.map((t) => (
        <button
          key={`${t.key}-${t.label}`}
          type="button"
          onClick={() => remove(t)}
          className="inline-flex items-center gap-1 rounded-full bg-fucsia/10 text-fucsia border border-fucsia/30 px-3 py-1 text-xs font-semibold hover:bg-fucsia/20 transition-colors dark:bg-fucsia/20 dark:text-cream"
          aria-label={`Rimuovi filtro ${t.label}`}
          data-testid={`active-filter-${slug(t.key + "-" + t.label)}`}
        >
          {t.label} <X size={12} aria-hidden />
        </button>
      ))}
    </div>
  );
}
